import { App } from 'antd';
import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ApiErrorEnum } from '../../enums/ApiErrorEnum';
import { FileDownloadStatusEnum } from '../../enums/FileDownloadStatusEnum';
import { FileLimitationEnum } from '../../enums/FileLimitationEnum';
import type File from '../../interfaces/File';
import {
  cancelAllDownloads,
  cancelDownload,
  getFilenameFromContentDisposition,
  isDownloadCancelled,
  normalizeDownloadResult,
  registerDownloadAbort,
  triggerBlobDownload,
  unregisterDownloadAbort,
} from '../../lib/fileDownload';
import { stopAllFileDownloadPolling } from '../../lib/fileDownload/fileDownloadPolling';
import { TransferService } from '../../services/transferApiService';
import { useAppStore } from '../../store/store';
import { getApiErrorCode, getApiErrorField } from '../../utils/apiErrorCode';
import { getAntivirusInfectedMessage } from '../../utils/antivirus';
import { generateUUID, getHashFromBytes, hasFileLimitation } from '../../utils/utils';

const transferService = new TransferService();

export const useFileDownload = () => {
  const { t } = useTranslation();
  const { notification, modal } = App.useApp();

  const fileDownloadProcessStatuses = useAppStore((state) => state.fileDownloadProcessStatuses);
  const isFileDownloadLoading = useAppStore((state) => state.isFileDownloadLoading);
  const setFileDownloadProcessStatuses = useAppStore(
    (state) => state.setFileDownloadProcessStatuses,
  );
  const setActiveFileDownloadProcessIds = useAppStore(
    (state) => state.setActiveFileDownloadProcessIds,
  );
  const setIsFileDownloadLoading = useAppStore((state) => state.setIsFileDownloadLoading);
  const resetFileDownloadState = useAppStore((state) => state.resetFileDownloadState);

  const handleDownloadFiles = useCallback(
    async (files: File[]): Promise<void> => {
      if (
        files.some((file) =>
          hasFileLimitation(
            file.fileLimitations,
            FileLimitationEnum.DenySensitiveOperationDownload,
          ),
        )
      ) {
        modal.warning({
          title: t('modal.tagsRequiredForDownload.title'),
          content: (
            <Trans>
              {t('modal.tagsRequiredForDownload.description', {
                resource: files?.[0]?.fileName,
                count: files.length,
              })}
            </Trans>
          ),
        });
        return;
      }

      setIsFileDownloadLoading(true);

      const fileIds = files.map((file) => file.id);
      const fileName = files?.[0]?.fileName || 'downloaded-file';
      const fakeProcessId = generateUUID();
      const isDirectFileDownload = files.length === 1 && !files[0]?.isFolder;

      setFileDownloadProcessStatuses((previousStatuses) => [
        ...previousStatuses,
        {
          additionalData: '',
          processId: fakeProcessId,
          progressPercentage: 0,
          status: FileDownloadStatusEnum.Processing,
          name: fileName,
          isDirectFileDownload,
        },
      ]);

      const abortController = registerDownloadAbort(fakeProcessId);

      try {
        const result: unknown = await transferService.download(
          { filesId: fileIds },
          {
            signal: abortController.signal,
            onDownloadProgress: (progressPercent) => {
              if (isDownloadCancelled(fakeProcessId)) {
                return;
              }
              setFileDownloadProcessStatuses((previousStatuses) =>
                previousStatuses.map((status) =>
                  status.processId === fakeProcessId
                    ? {
                        ...status,
                        status: FileDownloadStatusEnum.Processing,
                        progressPercentage: progressPercent,
                      }
                    : status,
                ),
              );
            },
          },
        );

        if (isDownloadCancelled(fakeProcessId)) {
          return;
        }

        const resultRecord = result as {
          kind?: string;
          status?: number;
          data?: unknown;
        };

        if (
          resultRecord?.kind === 'json' &&
          ((resultRecord.status ?? 0) < 200 || (resultRecord.status ?? 0) >= 300)
        ) {
          const errorCode = getApiErrorCode(resultRecord.data);
          switch (errorCode) {
            case ApiErrorEnum.AntiCaptureActivated:
              notification.error({ title: t('errors.antiCaptureActivated') });
              break;
            case ApiErrorEnum.DownloadRestrictionActivated:
              notification.error({ title: t('errors.downloadRestrictionActivated') });
              break;
            case ApiErrorEnum.WatermarkProtectionDownloadDenied:
              notification.error({
                title: t('errors.watermarkProtectionDownloadDenied'),
              });
              break;
            case ApiErrorEnum.AntivirusFileInfected: {
              const threatName =
                getApiErrorField(resultRecord.data, 'threatName') ??
                getApiErrorField(resultRecord.data, 'threat_name');
              notification.error({
                title: getAntivirusInfectedMessage(t, {
                  threatName,
                  fileName: files.length === 1 ? files[0]?.fileName : undefined,
                }),
              });
              break;
            }
            default:
              notification.error({ title: t('errors.genericError') });
          }
          setFileDownloadProcessStatuses((previousStatuses) =>
            previousStatuses.map((status) =>
              status.processId === fakeProcessId
                ? { ...status, status: FileDownloadStatusEnum.Failed, progressPercentage: 0 }
                : status,
            ),
          );
          return;
        }

        const normalized = normalizeDownloadResult(result);
        if (!normalized) {
          throw new Error('Unexpected download response shape');
        }

        if (normalized.type === 'blob') {
          const headers = normalized.headers || {};
          const contentDisposition =
            (headers['content-disposition'] as string) ||
            (headers['Content-Disposition'] as string) ||
            null;
          const inferredName =
            getFilenameFromContentDisposition(contentDisposition) || fileName || 'downloaded-file';

          const contentSha256Header =
            (headers['x-content-sha256'] as string) ||
            (headers['X-Content-SHA256'] as string) ||
            null;

          if (contentSha256Header) {
            if (isDownloadCancelled(fakeProcessId)) {
              return;
            }

            setFileDownloadProcessStatuses((previousStatuses) =>
              previousStatuses.map((status) =>
                status.processId === fakeProcessId
                  ? {
                      ...status,
                      status: FileDownloadStatusEnum.Verifying,
                      progressPercentage: 100,
                      name: inferredName,
                    }
                  : status,
              ),
            );

            try {
              const blobArrayBuffer = await normalized.blob.arrayBuffer();
              if (isDownloadCancelled(fakeProcessId)) {
                return;
              }

              const calculatedHash = await getHashFromBytes(blobArrayBuffer, false);
              const expectedHash = contentSha256Header.trim();
              const actualHash = calculatedHash.trim();

              if (expectedHash.toLowerCase() !== actualHash.toLowerCase()) {
                notification.error({
                  title: t('errors.fileIntegrityCheckFailed') || 'File integrity check failed',
                  description:
                    t('errors.fileIntegrityCheckFailedDescription') ||
                    'The downloaded file may be corrupted. Please try downloading again.',
                  duration: 0,
                });

                setFileDownloadProcessStatuses((previousStatuses) =>
                  previousStatuses.map((status) =>
                    status.processId === fakeProcessId
                      ? {
                          ...status,
                          status: FileDownloadStatusEnum.IntegrityCheckFailed,
                          progressPercentage: 100,
                          name: inferredName,
                        }
                      : status,
                  ),
                );
                return;
              }

              triggerBlobDownload(normalized.blob, inferredName);
              setFileDownloadProcessStatuses((previousStatuses) =>
                previousStatuses.map((status) =>
                  status.processId === fakeProcessId
                    ? {
                        ...status,
                        status: FileDownloadStatusEnum.Success,
                        progressPercentage: 100,
                        name: inferredName,
                        integrityVerified: true,
                      }
                    : status,
                ),
              );
              return;
            } catch (integrityError) {
              if (isDownloadCancelled(fakeProcessId)) {
                return;
              }
              const isAborted =
                integrityError instanceof Error &&
                (integrityError.name === 'AbortError' ||
                  (integrityError as { aborted?: boolean }).aborted === true);
              if (isAborted) {
                return;
              }
              notification.warning({
                title: t('errors.fileIntegrityCheckError'),
                description:
                  t('errors.fileIntegrityCheckErrorDescription') ||
                  'Could not verify file integrity, but download will proceed.',
              });
            }
          }

          if (isDownloadCancelled(fakeProcessId)) {
            return;
          }

          triggerBlobDownload(normalized.blob, inferredName);
          setFileDownloadProcessStatuses((previousStatuses) =>
            previousStatuses.map((status) =>
              status.processId === fakeProcessId
                ? {
                    ...status,
                    status: FileDownloadStatusEnum.Success,
                    progressPercentage: 100,
                    name: inferredName,
                  }
                : status,
            ),
          );
          return;
        }

        if (normalized.type === 'process') {
          const realProcessId = normalized.processId;
          setActiveFileDownloadProcessIds((previousIds) => [...previousIds, realProcessId]);
          setFileDownloadProcessStatuses((previousStatuses) =>
            previousStatuses.map((status) =>
              status.processId === fakeProcessId
                ? {
                    ...status,
                    processId: realProcessId,
                    name: fileName,
                    isDirectFileDownload: false,
                  }
                : status,
            ),
          );
        }
      } catch (error) {
        if (isDownloadCancelled(fakeProcessId)) {
          return;
        }
        const isAborted =
          error instanceof Error &&
          (error.name === 'AbortError' || (error as { aborted?: boolean }).aborted === true);
        if (isAborted) {
          return;
        }
        console.error('Catch error', error);
        setFileDownloadProcessStatuses((previousStatuses) =>
          previousStatuses.map((status) =>
            status.processId === fakeProcessId
              ? {
                  ...status,
                  status: FileDownloadStatusEnum.Failed,
                  progressPercentage: 0,
                  name: fileName,
                }
              : status,
          ),
        );
      } finally {
        unregisterDownloadAbort(fakeProcessId);
        setIsFileDownloadLoading(false);
      }
    },
    [
      modal,
      notification,
      setActiveFileDownloadProcessIds,
      setFileDownloadProcessStatuses,
      setIsFileDownloadLoading,
      t,
    ],
  );

  const handleCancelFileDownloadProcess = useCallback(
    async (processId: string): Promise<void> => {
      try {
        cancelDownload(processId);
        stopAllFileDownloadPolling();
        setActiveFileDownloadProcessIds((previousIds) =>
          previousIds.filter((id) => id !== processId),
        );
        setFileDownloadProcessStatuses((previousStatuses) => {
          const index = previousStatuses.findIndex((status) => status.processId === processId);
          const failedProcess = {
            additionalData: '',
            processId,
            progressPercentage: 0,
            status: FileDownloadStatusEnum.Failed,
          };
          if (index >= 0) {
            const nextStatuses = [...previousStatuses];
            nextStatuses[index] = failedProcess;
            return nextStatuses;
          }
          return [...previousStatuses, failedProcess];
        });
      } catch (error) {
        console.error(`Error cancelling process ${processId}:`, error);
      }
    },
    [setActiveFileDownloadProcessIds, setFileDownloadProcessStatuses],
  );

  const handleCancelAllFileDownloads = useCallback(async (): Promise<void> => {
    const processIds = useAppStore
      .getState()
      .fileDownloadProcessStatuses.map((status) => status.processId);
    const activeProcessIds = useAppStore.getState().activeFileDownloadProcessIds;
    cancelAllDownloads([...new Set([...processIds, ...activeProcessIds])]);
    stopAllFileDownloadPolling();
    resetFileDownloadState();
  }, [resetFileDownloadState]);

  return {
    fileDownloadProcessStatuses,
    isFileDownloadLoading,
    handleDownloadFiles,
    handleCancelFileDownloadProcess,
    handleCancelAllFileDownloads,
  };
};
